import type { OnsetEnvelope } from './analysis';
import { channelsToBuffer, sliceChannels } from './warp';

/**
 * Finding loops inside a full track.
 *
 * The question "is this a good four-bar loop?" is mostly the question "does
 * this four bars repeat?" — so the primary signal is how well a bar-aligned
 * window correlates with the window immediately after it. A drum break that
 * comes round again scores high; a one-off fill, a vocal phrase or a build
 * scores low. Energy and a hit on the downbeat break the remaining ties.
 */

export interface LoopCandidate {
  /** Bar number from the grid origin, 1-based, as shown to the user. */
  bar: number;
  startSec: number;
  endSec: number;
  /** Normalised correlation with the following window, 0..1. */
  repetition: number;
  /** Mean onset energy relative to the track, 0..1. */
  energy: number;
  /** Combined ranking, 0..1. */
  score: number;
}

export interface CandidateOptions {
  bpm: number;
  /** Seconds to the first downbeat. */
  offsetSec: number;
  beatsPerBar: number;
  /** Loop length in bars. */
  bars: number;
  /** Total file duration, seconds. */
  duration: number;
  limit?: number;
}

function correlate(values: Float32Array, a: number, b: number, len: number): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < len; i++) {
    const x = values[a + i] ?? 0;
    const y = values[b + i] ?? 0;
    dot += x * y;
    na += x * x;
    nb += y * y;
  }
  const denom = Math.sqrt(na * nb);
  return denom > 1e-9 ? Math.max(0, dot / denom) : 0;
}

export function findLoopCandidates(
  env: OnsetEnvelope,
  opts: CandidateOptions,
): LoopCandidate[] {
  const { bpm, offsetSec, beatsPerBar, bars, duration } = opts;
  const limit = opts.limit ?? 8;
  const { values, rate, origin } = env;

  const barSec = (60 / bpm) * beatsPerBar;
  const loopSec = barSec * bars;
  const loopFrames = Math.max(4, Math.round(loopSec * rate));
  if (loopSec <= 0 || loopSec >= duration) return [];

  let globalMean = 0;
  for (let i = 0; i < values.length; i++) globalMean += values[i];
  globalMean /= Math.max(1, values.length);
  if (globalMean <= 0) return [];

  const totalBars = Math.floor((duration - offsetSec) / barSec);
  const scored: LoopCandidate[] = [];

  for (let bar = 0; bar + bars <= totalBars; bar++) {
    const startSec = offsetSec + bar * barSec;
    const endSec = startSec + loopSec;
    if (endSec > duration) break;

    const a = Math.round((startSec - origin) * rate);
    if (a < 0) continue;
    const b = a + loopFrames;

    // Comparing against the *next* window means the last candidate in a track
    // has nothing to compare to; fall back to the previous window there.
    const hasNext = b + loopFrames <= values.length;
    const repetition = hasNext
      ? correlate(values, a, b, loopFrames)
      : a - loopFrames >= 0
        ? correlate(values, a - loopFrames, a, loopFrames)
        : 0;

    let sum = 0;
    for (let i = 0; i < loopFrames && a + i < values.length; i++) sum += values[a + i];
    const energy = Math.min(1, sum / loopFrames / globalMean);

    // A loop that doesn't start on a hit will always feel a beat late.
    let downbeatHit = 0;
    for (let i = -1; i <= 2; i++) downbeatHit = Math.max(downbeatHit, values[a + i] ?? 0);

    const score = 0.58 * repetition + 0.27 * energy + 0.15 * Math.min(1, downbeatHit * 1.6);
    scored.push({ bar: bar + 1, startSec, endSec, repetition, energy, score });
  }

  // Keep the best, then clear a neighbourhood around it. Suggestions are only
  // useful if they point at *different* parts of the track — on material that
  // repeats every two bars, the raw top eight are otherwise the same passage
  // over and over. Eight bars of separation is the floor even for short loops.
  const spacing = Math.max(loopSec, barSec * 8);
  scored.sort((x, y) => y.score - x.score);
  const picked: LoopCandidate[] = [];
  for (const c of scored) {
    if (picked.length >= limit) break;
    if (picked.some((p) => Math.abs(p.startSec - c.startSec) < spacing)) continue;
    picked.push(c);
  }
  return picked.sort((x, y) => x.startSec - y.startSec);
}

/** Cuts a region out of a decoded file as a standalone loop. */
export function extractLoop(
  ctx: BaseAudioContext,
  buffer: AudioBuffer,
  startSec: number,
  endSec: number,
): AudioBuffer {
  const channels = sliceChannels(buffer, startSec, endSec);
  return channelsToBuffer(ctx, channels, buffer.sampleRate);
}
